import * as React from 'react'
import { Paper, Table, TableBody, TableContainer, Checkbox, Typography, Box } from '@mui/material'

import { useBulkImportStore } from '../../hooks/useBulkImportStore'
import { type AllowedCardTypesBulkImport, type ModalBulkType } from '../../store/bulk-import.store.types'

import { columns } from './columns'
import ToolbarTableBulkImport from './ToolbarTableBulkImport'
import HeaderTableBulkImport from './HeaderTableBulkImport'
import { StyledCardTableCell, StyledCardTableRow } from './StyledTableBulkImport'
import { getComparator } from './utils'

import { ReactComponent as CheckBoxIcon } from '@/assets/icons/checkbox-icon.svg'
import { ReactComponent as CheckBoxCheckedIcon } from '@/assets/icons/checkbox-checked-filled-icon.svg'
import { type CardActivityWithId, type CardActivityType } from '@/models/card-activity/card-activity.model'

interface TableBulkImportProps {
  onToggleBulkModals: (params: { open: boolean; type: ModalBulkType }) => void
  onSelectCard: (cardIds: string) => void
  onCellUpdate: (
    updates: {
      cardId: string
      fieldToUpdate: keyof CardActivityWithId
      updates: any
    }[],
  ) => void
  handleSelectAllCards: (cardIds: string[]) => void
  selectedCards: string[]
}

export default function TableBulkImport({
  onToggleBulkModals = () => null,
  onSelectCard = () => null,
  onCellUpdate = () => null,
  handleSelectAllCards = () => null,
  selectedCards = [],
}: TableBulkImportProps) {
  const { cards, cardType } = useBulkImportStore()

  const [order, setOrder] = React.useState<'asc' | 'desc' | 'none'>('none')
  const [orderBy, setOrderBy] = React.useState<keyof CardActivityWithId | ''>('')
  const [searchValue, setSearchValue] = React.useState<string>('')

  const rows = Array.from(cards.values())

  const handleUpdateCard = (id: string, field: keyof CardActivityWithId, value: Partial<CardActivityWithId>) => {
    onCellUpdate([
      {
        cardId: id,
        fieldToUpdate: field,
        updates: value,
      },
    ])
  }

  const handleRequestSort = (_: React.MouseEvent<unknown>, property: keyof CardActivityWithId) => {
    if (orderBy === property) {
      // Cycle through: asc → desc → none
      if (order === 'asc') {
        setOrder('desc')
      } else if (order === 'desc') {
        setOrder('none')
        setOrderBy('') // Reset orderBy when going to none state
      } else {
        setOrder('asc')
        setOrderBy(property)
      }
    } else {
      // New column, start with ascending
      setOrder('asc')
      setOrderBy(property)
    }
  }

  const handleSelectAllClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const newSelected = rows.map(n => n.id)

      handleSelectAllCards(newSelected)
    } else {
      handleSelectAllCards([])
    }
  }

  const handleClick = (_: React.MouseEvent<unknown>, cardId: string) => {
    onSelectCard(cardId)
  }

  const isSelected = (id: string) => selectedCards.includes(id)

  const visibleRows = React.useMemo(() => {
    const filtered = rows.filter(row => JSON.stringify(row).toLowerCase().includes(searchValue.toLowerCase()))

    // Only apply sorting if order is not 'none' and orderBy is set
    if (order === 'none' || orderBy === '') {
      return filtered // Preserve insertion order
    }

    return filtered.sort(getComparator(order, orderBy))
  }, [rows, order, orderBy, searchValue])

  const filteredColumns = React.useMemo(() => {
    return columns.filter(col => col.types.includes(cardType as AllowedCardTypesBulkImport))
  }, [cardType])

  const visibleColumnIds = React.useMemo(() => {
    return filteredColumns.map(col => col.id)
  }, [filteredColumns])

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Paper
        sx={{
          width: '100%',
          borderRadius: '16px',
          border: '1px solid #D0D5DD',
        }}
      >
        <ToolbarTableBulkImport
          cardType={cardType as CardActivityType}
          numSelected={selectedCards.length}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onToggleBulkModals={onToggleBulkModals}
        />

        <TableContainer
          sx={{
            overflowX: 'auto',
            maxHeight: 'calc(100vh - 220px)',
          }}
        >
          <Table stickyHeader aria-labelledby="tableTitle" size="medium" sx={{ minWidth: 750 }}>
            <HeaderTableBulkImport
              numSelected={selectedCards.length}
              order={order}
              orderBy={orderBy}
              rowCount={rows.length}
              visibleColumnIds={visibleColumnIds}
              onRequestSort={handleRequestSort}
              onSelectAllClick={handleSelectAllClick}
            />

            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = isSelected(row.id)
                const labelId = `dynamic-table-checkbox-${index.toString()}`

                return (
                  <StyledCardTableRow
                    key={row.id}
                    hover
                    aria-checked={isItemSelected}
                    role="checkbox"
                    selected={isItemSelected}
                    tabIndex={-1}
                    onClick={event => {
                      handleClick(event, row.id)
                    }}
                  >
                    <StyledCardTableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        checkedIcon={<CheckBoxCheckedIcon />}
                        color="primary"
                        icon={<CheckBoxIcon />}
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </StyledCardTableCell>

                    {filteredColumns.map(col => (
                      <StyledCardTableCell key={col.id}>
                        {typeof col.renderCell === 'function' ? (
                          col.renderCell({ row, handleUpdateCard })
                        ) : (
                          <Typography color="#475467" fontSize={12} lineHeight="18px">
                            {(() => {
                              const value = row[col.id]

                              if (value === null || value === undefined) return ''
                              if (typeof value === 'object') return JSON.stringify(value)

                              return String(value)
                            })()}
                          </Typography>
                        )}
                      </StyledCardTableCell>
                    ))}
                  </StyledCardTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  )
}
